/* ============================================================
   FQ CINEMA — motion layer
   The Feuquingue Cookbook / zefeuquinguecookbook.com
   Rich on every breakpoint, including mobile (by brief).
   Honours prefers-reduced-motion.
   Loaded site-wide with `defer` from layout/theme.liquid
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var doc = document;
  var body = doc.body;

  /* ---------- helpers ---------- */
  function each(sel, root, fn) {
    var nodes = (root || doc).querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) fn(nodes[i], i);
  }

  function make(tag, cls) {
    var el = doc.createElement(tag);
    if (cls) el.className = cls;
    return el;
  }

  /* ---------- 0. scroll container ----------
     Horizon does NOT scroll the window: `.page-wrapper` is a
     flex column with its own `overflow:auto`. Every scroll-driven
     effect has to read from that element, not from window.scrollY. */
  var SC = null;

  /* Coarse pointer / small screen: skip the effects whose cost is per-event
     or per-frame and whose payoff needs a mouse. */
  var FQMOB = window.matchMedia("(max-width:749px),(pointer:coarse)").matches;

  /* Cached so the scroll handler never queries or measures the DOM. */
  var FQPB = null;
  var FQHERO = null;
  var FQHH = 0;
  var FQMAX = 0;
  var FQSP = -1;
  var FQTICK = 0;
  var FQSCROLLED = false;
  var FQCTA = false;

  function scroller() {
    var w = doc.querySelector('.page-wrapper');
    if (w && w.scrollHeight - w.clientHeight > 40) return w;
    return null; // null === window/document scrolling
  }

  function scrollTop() {
    return SC ? SC.scrollTop : (window.scrollY || doc.documentElement.scrollTop);
  }

  function scrollMax() {
    return SC
      ? SC.scrollHeight - SC.clientHeight
      : doc.documentElement.scrollHeight - vh;
  }

  /* ---------- 1. chrome: scroll progress ---------- */
  function chrome() {
    // legacy: strip any curtain left by an earlier build
    each('.fqc-curtain', doc, function (el) { el.parentNode.removeChild(el); });

    if (!doc.querySelector('.fqc-progress')) body.appendChild(make('div', 'fqc-progress'));

    requestAnimationFrame(function () {
      requestAnimationFrame(function () { body.classList.add('fqc-ready'); });
    });
  }

  /* ---------- 2. what gets revealed ---------- */
  var REVEAL = [
    '.fqb__heroGrid > *',
    '.fqb__coverBox',
    '.fqb__secHead',
    '.fqb__closer > *',
    '.product-information__media',
    '.product-details .group-block-content > *',
    '.shopify-section [id*="product_recommendations"] .product-media-container',
    'footer .section > *'
  ].join(',');

  var STAGGER = [
    '.fqb__credsGrid',
    '.fqb__insideGrid',
    '.fqb__fGrid',
    '.fqb__qGrid',
    '.fqb__faqList'
  ].join(',');

  function tag() {
    each(REVEAL, doc, function (el) {
      if (!el.closest('.fqc-stagger')) el.classList.add('fqc-r');
    });

    each(STAGGER, doc, function (grid) {
      grid.classList.add('fqc-stagger');
      each(':scope > *', grid, function (child, i) {
        child.style.setProperty('--fqc-i', i);
      });
    });
  }

  /* ---------- 3. headline line-by-line reveal ---------- */
  function splitHeadlines() {
    each('.fqb__hero h1, .fqb__closer h2, .fqb__secHead h2', doc, function (h) {
      if (h.dataset.fqcSplit) return;
      h.dataset.fqcSplit = '1';

      var words = (h.textContent || '').trim().split(/\s+/);
      if (!words.length) return;

      h.textContent = '';
      // one masked line per word-group keeps it robust across wraps
      words.forEach(function (w, i) {
        var line = make('span', 'fqc-line');
        var inner = make('span');
        inner.textContent = w;
        line.style.setProperty('--fqc-i', i);
        line.style.display = 'inline-block';
        line.style.marginRight = '0.24em';
        line.appendChild(inner);
        h.appendChild(line);
      });
      h.classList.add('fqc-r');
    });
  }

  /* ---------- 4. intersection reveals ---------- */
  function observe() {
    if (!('IntersectionObserver' in window)) {
      each('.fqc-r, .fqc-stagger', doc, function (el) { el.classList.add('fqc-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('fqc-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    each('.fqc-r, .fqc-stagger', doc, function (el) { io.observe(el); });

    /* Failsafe. Every reveal target starts at opacity 0, so if the observer
       never fires -- a throttled tab that is closed before it is looked at,
       a layout that keeps a target permanently out of the root, an engine
       quirk -- the page would stay blank. After 2.2s, reveal anything in or
       above the current viewport regardless of what the observer thinks. */
    setTimeout(function () {
      each('.fqc-r, .fqc-stagger', doc, function (el) {
        if (el.classList.contains('fqc-in')) return;
        var r = el.getBoundingClientRect();
        if (r.top < (window.innerHeight || 0)) {
          el.classList.add('fqc-in');
          io.unobserve(el);
        }
      });
    }, 2200);
  }

  /* ---------- 5. parallax + header state + progress ---------- */
  var parallaxNodes = [];

  function collectParallax() {
    parallaxNodes = [];
    var defs = [
      ['.fqb__coverBox', -0.10],
      ['.fqb__hero::bg', 0],           // placeholder, skipped
      ['.product-information__media', -0.05],
      ['.fqb__closer', -0.06]
    ];
    defs.forEach(function (d) {
      if (d[0].indexOf('::') > -1) return;
      each(d[0], doc, function (el) {
        el.classList.add('fqc-par');
        parallaxNodes.push({ el: el, k: d[1] });
      });
    });
  }

  var ticking = false;
  var vh = window.innerHeight;

  function onScroll() {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(function () {
      /* Reads first, writes second. Interleaving them forces a synchronous
         style + layout flush on every single scroll frame. */
      var y = scrollTop();

      if (FQTICK++ % 30 === 0) {
        FQMAX = scrollMax();
        if (!FQPB) FQPB = doc.querySelector(".fqc-progress");
        if (!FQHERO) FQHERO = doc.querySelector(".fqb__hero");
        if (FQHERO) FQHH = FQHERO.offsetHeight;
      }

      var doPar = !REDUCED && !FQMOB && parallaxNodes.length > 0;
      var rects = null;
      if (doPar) {
        rects = [];
        for (var i = 0; i < parallaxNodes.length; i++) {
          rects.push(parallaxNodes[i].el.getBoundingClientRect());
        }
      }

      /* ---- writes ---- */

      // header solidify. Guarded: a class on <body> invalidates style for
      // every descendant, so only touch it when the state actually flips.
      var scrolled = y > 24;
      if (scrolled !== FQSCROLLED) {
        FQSCROLLED = scrolled;
        body.classList.toggle("fqc-scrolled", scrolled);
      }

      // Scroll progress as a composited transform on the one bar element,
      // rather than a custom property on <html>, which invalidated style
      // for the whole document on every frame.
      var sp = FQMAX > 0 ? y / FQMAX : 0;
      if (sp < 0) sp = 0;
      if (sp > 1) sp = 1;
      if (FQPB && (sp > FQSP + 0.0015 || sp < FQSP - 0.0015)) {
        FQSP = sp;
        FQPB.style.transform = "scaleX(" + sp.toFixed(4) + ")";
      }

      // sticky CTA appears after the hero
      if (FQHERO) {
        var ctaOn = y > FQHH * 0.62;
        if (ctaOn !== FQCTA) {
          FQCTA = ctaOn;
          body.classList.toggle("fqc-cta-on", ctaOn);
        }
      }

      // Parallax: desktop only. On touch it costs a rect read plus a custom
      // property write per node per frame, for a few px of drift.
      if (doPar) {
        for (var j = 0; j < parallaxNodes.length; j++) {
          var r = rects[j];
          if (r.bottom < -200 || r.top > vh + 200) continue;
          var mid = r.top + r.height / 2 - vh / 2;
          parallaxNodes[j].el.style.setProperty("--fqc-p", (mid * parallaxNodes[j].k).toFixed(2));
        }
      }

      ticking = false;
    });
  }

  /* ---------- 6. cover tilt on pointer + device orientation ---------- */
  function coverTilt() {
    if (REDUCED) return;
    var box = doc.querySelector('.fqb__coverBox');
    if (!box) return;
    var img = box.querySelector('img');
    if (!img) return;

    function apply(rx, ry) {
      img.style.transform = 'rotateY(' + ry.toFixed(2) + 'deg) rotateX(' + rx.toFixed(2) + 'deg)';
    }

    if (box.__fqTilt) return;
    box.__fqTilt = true;

    // One composited write per frame, however fast the events arrive.
    var traf = 0, trx = 0, trY = 0;
    function schedule(rx, ry) {
      trx = rx; trY = ry;
      if (traf) return;
      traf = requestAnimationFrame(function () { traf = 0; apply(trx, trY); });
    }

    // desktop pointer
    box.addEventListener("pointermove", function (e) {
      if (e.pointerType && e.pointerType !== "mouse") return;
      var r = box.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - 0.5;
      var py = (e.clientY - r.top) / r.height - 0.5;
      schedule(-py * 9, px * 14);
    });

    box.addEventListener("pointerleave", function () {
      if (traf) { cancelAnimationFrame(traf); traf = 0; }
      img.style.transform = "";
    });

    // mobile: gyroscope, so the effect survives on touch devices
    if (window.DeviceOrientationEvent && !("ontouchstart" in window === false)) {
      window.addEventListener("deviceorientation", function (e) {
        if (e.gamma == null || e.beta == null) return;
        var ry = Math.max(-14, Math.min(14, e.gamma * 0.45));
        var rx = Math.max(-8, Math.min(8, (e.beta - 45) * 0.18));
        schedule(rx, ry);
      }, true);
    }
  }

  /* ---------- 7. magnetic buttons ---------- */
  function magnetic() {
    // A pointermove listener on every button fires all the way through a
    // touch-scroll, forcing a layout read plus a style write per event.
    // The effect follows a mouse cursor, so it has no meaning on touch.
    if (REDUCED || FQMOB) return;
    each(".fqb__btn, .button", doc, function (btn) {
      if (btn.closest(".fqb__stickyCta")) return;
      if (btn.__fqMag) return; // rewire() runs again on every section render
      btn.__fqMag = true;
      var raf = 0, mx = 0, my = 0;
      btn.addEventListener("pointermove", function (e) {
        if (e.pointerType && e.pointerType !== "mouse") return;
        var r = btn.getBoundingClientRect();
        mx = (e.clientX - r.left - r.width / 2) * 0.14;
        my = (e.clientY - r.top - r.height / 2) * 0.22;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          btn.style.translate = mx.toFixed(1) + "px " + my.toFixed(1) + "px";
        });
      });
      btn.addEventListener("pointerleave", function () { btn.style.translate = ""; });
    });
  }

  /* ---------- 8. re-run after Shopify section re-renders ---------- */
  function rewire() {
    tag();
    splitHeadlines();
    observe();
    collectParallax();
    magnetic();
    FQHERO = null;
    FQPB = null;
    FQTICK = 0;
    coverTilt();
    onScroll();
  }

  function boot() {
    SC = scroller();
    chrome();
    rewire();

    if (SC) SC.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      vh = window.innerHeight;
      FQMOB = window.matchMedia("(max-width:749px),(pointer:coarse)").matches;
      FQTICK = 0; // refresh the cached measurements on the next frame
      var next = scroller();
      if (next !== SC) {
        if (SC) SC.removeEventListener('scroll', onScroll);
        SC = next;
        if (SC) SC.addEventListener('scroll', onScroll, { passive: true });
      }
      onScroll();
    }, { passive: true });

    // sections can grow after images load; re-resolve the scroller once
    window.addEventListener('load', function () {
      if (!SC) {
        SC = scroller();
        if (SC) SC.addEventListener('scroll', onScroll, { passive: true });
      }
      FQTICK = 0;
      onScroll();
    });

    // Horizon / Shopify swap section HTML on cart + variant changes
    doc.addEventListener('shopify:section:load', rewire);
    doc.addEventListener('shopify:section:select', rewire);
    doc.addEventListener('variant:update', rewire);

    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes.length) { rewire(); return; }
      }
    });
    var drawer = doc.getElementById('shopify-section-cart-drawer-section');
    if (drawer) mo.observe(drawer, { childList: true, subtree: true });
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();




/* ===== fq-anchor: reliable in-page anchor scrolling =====
   Horizon scrolls DIV.page-wrapper, not the window. Native fragment
   navigation plus scroll-behavior:smooth stalls on it, so we own the
   scroll and frame the section's content block instead of its padding. */
(function () {
  function fqScroller() {
    var pw = document.querySelector('.page-wrapper');
    if (pw && pw.scrollHeight > pw.clientHeight + 4) return pw;
    return document.scrollingElement || document.documentElement;
  }
  function headerClear() {
    var h = document.querySelector('.header-wrapper, .header, header');
    if (!h) return 0;
    var p = getComputedStyle(h).position;
    if (p !== 'fixed' && p !== 'sticky') return 0;
    return Math.round(h.getBoundingClientRect().height);
  }
  function destFor(el) {
    var sc = fqScroller();
    var inner = el;
    if (el.children.length === 1 && el.children[0].getBoundingClientRect().height > 40) inner = el.children[0];
    var isRoot = (sc === document.documentElement || sc === document.body);
    var scTop = isRoot ? 0 : sc.getBoundingClientRect().top;
    var vh = sc.clientHeight || window.innerHeight;
    var r = inner.getBoundingClientRect();
    var absTop = r.top - scTop + sc.scrollTop;
    var gap = headerClear() + 24;
    var want = (r.height < vh - gap) ? Math.max(gap, (vh - r.height) / 2) : gap;
    var max = Math.max(0, sc.scrollHeight - sc.clientHeight);
    return Math.max(0, Math.min(absTop - want, max));
  }
  var raf = null, seq = 0;
  function glide(sc, to, ms) {
    var mine = ++seq;
    if (raf) { cancelAnimationFrame(raf); raf = null; }
    var from = sc.scrollTop, delta = to - from, t0 = null;
    var prev = sc.style.scrollBehavior;
    sc.style.scrollBehavior = 'auto';
    function done() { raf = null; sc.style.scrollBehavior = prev; sc.removeEventListener('wheel', bail); sc.removeEventListener('touchstart', bail); }
    function bail() { if (mine === seq) { seq++; if (raf) cancelAnimationFrame(raf); done(); } }
    var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || Math.abs(delta) < 2) { sc.scrollTop = to; done(); return; }
    sc.addEventListener('wheel', bail, { passive: true });
    sc.addEventListener('touchstart', bail, { passive: true });
    function ease(p) { return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2; }
    function step(ts) {
      if (mine !== seq) return;
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / ms);
      sc.scrollTop = from + delta * ease(p);
      if (p < 1) { raf = requestAnimationFrame(step); } else { done(); }
    }
    raf = requestAnimationFrame(step);
    setTimeout(function () {
      if (mine === seq && raf !== null) { cancelAnimationFrame(raf); sc.scrollTop = to; done(); }
    }, ms + 500);
  }
  function goTo(el) {
    var sc = fqScroller();
    var dest = destFor(el);
    var dur = Math.min(1100, Math.max(420, Math.abs(dest - sc.scrollTop) * 0.5));
    glide(sc, dest, dur);
    setTimeout(function () {
      var d2 = destFor(el);
      if (Math.abs(d2 - sc.scrollTop) > 10) glide(sc, d2, 340);
    }, dur + 700);
  }
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
    var a = t.closest('a[href*="#"]');
    if (!a || a.hasAttribute('aria-controls') || a.getAttribute('role') || a.target === '_blank') return;
    var href = a.getAttribute('href') || '';
    var i = href.indexOf('#');
    if (i < 0) return;
    var id = href.slice(i + 1);
    if (!id) return;
    if (i > 0) {
      try { if (new URL(href, location.href).pathname !== location.pathname) return; } catch (err) { return; }
    }
    var el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    goTo(el);
    try { history.replaceState(null, '', '#' + id); } catch (err) {}
  }, true);
  function onHash() {
    if (!location.hash || location.hash.length < 2) return;
    var el = document.getElementById(location.hash.slice(1));
    if (el) setTimeout(function () { goTo(el); }, 320);
  }
  if (document.readyState === 'complete') onHash();
  else window.addEventListener('load', onHash);
})();
